import type {
  CommitDeclaration,
  CommitReceipt,
  CommitTransitionOutcomeDeclaration,
  EffectCommitRequest,
  EffectProjectionResult,
  ProjectionMapper,
  ProjectionResult,
  ToolWithHandler,
  TransitionProjectionResult,
} from "./types";

const MAX_PROJECTION_CHANGES = 1_000;
const MAX_SAFE_SUMMARY_KEYS = 32;
const MAX_SAFE_TEXT_LENGTH = 2_000;

export interface StateMachineDefinition {
  name: string;
  label?: string;
  states: Record<string, { label?: string; isFinal?: boolean }>;
  transitions: Record<
    string,
    {
      label?: string;
      from: string;
      effect: ToolWithHandler;
      outcomes: Record<string, CommitTransitionOutcomeDeclaration>;
    }
  >;
}

export type DefinedStateMachine<
  TDefinition extends StateMachineDefinition = StateMachineDefinition,
> = TDefinition & { transitions: TDefinition["transitions"] };

export interface DefinedTransitionMetadata {
  machine: string;
  transition: string;
}

const definedTransitionMetadata = new WeakMap<
  object,
  DefinedTransitionMetadata
>();

/** @internal Used by environment-level transition acknowledgements. */
export function getDefinedTransitionMetadata(
  transition: object,
): DefinedTransitionMetadata | undefined {
  return definedTransitionMetadata.get(transition);
}

export interface CommitTransport {
  persist(request: EffectCommitRequest): Promise<CommitReceipt>;
  mappingFailed?(input: {
    effectKey: string;
    effectName: string;
    invocationId: string;
    idempotencyKey: string;
    environmentId: string;
    message: string;
  }): Promise<void>;
}

export function defineProjection<
  TResult,
  TProjection extends ProjectionResult = ProjectionResult,
>(
  mapper: ProjectionMapper<TResult, TProjection>,
): ProjectionMapper<TResult, TProjection> {
  return mapper;
}

export function defineEffect<TEffect extends ToolWithHandler>(
  effect: TEffect,
): TEffect {
  return effect;
}

export function defineStateMachine<
  const TDefinition extends StateMachineDefinition,
>(definition: TDefinition): DefinedStateMachine<TDefinition> {
  const stateNames = new Set(Object.keys(definition.states));
  for (const [transitionName, transition] of Object.entries(
    definition.transitions,
  )) {
    if (!stateNames.has(transition.from)) {
      throw new Error(
        `Transition ${transitionName} starts at undeclared state ${transition.from}`,
      );
    }
    if (transition.effect.commit?.kind !== "transition") {
      throw new Error(
        `Transition ${transitionName} must use a transition-commit effect`,
      );
    }
    const outcomes = Object.entries(transition.outcomes);
    const primary = outcomes.filter(([, outcome]) => outcome.primary === true);
    if (primary.length !== 1) {
      throw new Error(
        `Transition ${transitionName} must declare exactly one primary outcome`,
      );
    }
    for (const [outcomeKey, outcome] of outcomes) {
      if (outcome.to !== "$current" && !stateNames.has(outcome.to)) {
        throw new Error(
          `Transition ${transitionName} outcome ${outcomeKey} targets undeclared state ${outcome.to}`,
        );
      }
      if (outcome.to === "$current" && outcome.disposition !== "error") {
        throw new Error(
          `Transition ${transitionName} outcome ${outcomeKey} may use $current only with error disposition`,
        );
      }
    }
  }
  for (const [transitionName, transition] of Object.entries(
    definition.transitions,
  )) {
    definedTransitionMetadata.set(transition, {
      machine: definition.name,
      transition: transitionName,
    });
  }
  return definition as DefinedStateMachine<TDefinition>;
}

function requireNonEmptyString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value.trim();
}

function validateObjectReference(
  value: unknown,
  path: string,
): asserts value is { className: string; id: string; path?: string } {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object reference`);
  }
  const reference = value as Record<string, unknown>;
  requireNonEmptyString(reference.className, `${path}.className`);
  requireNonEmptyString(reference.id, `${path}.id`);
  if (reference.path !== undefined) {
    requireNonEmptyString(reference.path, `${path}.path`);
  }
}

function validateScalarRecord(
  value: unknown,
  path: string,
): asserts value is Record<string, string | number | boolean | null> {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    ![Object.prototype, null].includes(Object.getPrototypeOf(value))
  ) {
    throw new Error(`${path} must be an object of scalar values`);
  }
  for (const [key, item] of Object.entries(value)) {
    if (
      item !== null &&
      typeof item !== "string" &&
      typeof item !== "number" &&
      typeof item !== "boolean"
    ) {
      throw new Error(
        `${path}.${key} must be a string, finite number, boolean, or null`,
      );
    }
    if (typeof item === "number" && !Number.isFinite(item)) {
      throw new Error(`${path}.${key} must be finite`);
    }
  }
}

function validateProjectedRecord(value: unknown, path: string): void {
  validateObjectReference(value, path);
  const record = value as unknown as Record<string, unknown>;
  if (record.label !== undefined) {
    if (typeof record.label !== "string") {
      throw new Error(`${path}.label must be a string`);
    }
    if (record.label.length > MAX_SAFE_TEXT_LENGTH) {
      throw new Error(
        `${path}.label exceeds ${MAX_SAFE_TEXT_LENGTH} characters`,
      );
    }
  }
  validateScalarRecord(record.fields, `${path}.fields`);
}

function validateBoundedJson(value: unknown, path: string, depth = 0): void {
  if (depth > 12) throw new Error(`${path} is nested too deeply`);
  if (value === null || typeof value === "boolean") return;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error(`${path} must be finite`);
    return;
  }
  if (typeof value === "string") {
    if (value.length > MAX_SAFE_TEXT_LENGTH) {
      throw new Error(`${path} exceeds ${MAX_SAFE_TEXT_LENGTH} characters`);
    }
    return;
  }
  if (Array.isArray(value)) {
    if (value.length > MAX_PROJECTION_CHANGES) {
      throw new Error(`${path} contains too many values`);
    }
    value.forEach((item, index) =>
      validateBoundedJson(item, `${path}[${index}]`, depth + 1),
    );
    return;
  }
  if (!value || typeof value !== "object") {
    throw new Error(`${path} contains an unsupported value`);
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length > 256) throw new Error(`${path} contains too many keys`);
  for (const [key, item] of entries) {
    if (/token|secret|password|authorization|cookie/i.test(key)) {
      throw new Error(`${path}.${key} is not allowed in a commit projection`);
    }
    validateBoundedJson(item, `${path}.${key}`, depth + 1);
  }
}

export function validateProjectionResult(
  declaration: CommitDeclaration<any>,
  projection: ProjectionResult,
): void {
  if (!projection || typeof projection !== "object") {
    throw new Error("Projection mapper must return an object");
  }
  requireNonEmptyString(
    projection.source?.reference,
    "projection.source.reference",
  );
  if (projection.source.version !== undefined) {
    requireNonEmptyString(
      projection.source.version,
      "projection.source.version",
    );
  }
  if (!Array.isArray(projection.changes)) {
    throw new Error("projection.changes must be an array");
  }
  if (projection.changes.length > MAX_PROJECTION_CHANGES) {
    throw new Error(
      `projection.changes exceeds the ${MAX_PROJECTION_CHANGES} change limit`,
    );
  }
  if (projection.primaryTarget) {
    validateObjectReference(
      projection.primaryTarget,
      "projection.primaryTarget",
    );
  }
  if (projection.safeSummary) {
    const entries = Object.entries(projection.safeSummary);
    if (entries.length > MAX_SAFE_SUMMARY_KEYS) {
      throw new Error(
        `projection.safeSummary exceeds the ${MAX_SAFE_SUMMARY_KEYS} key limit`,
      );
    }
    validateBoundedJson(projection.safeSummary, "projection.safeSummary");
    validateScalarRecord(projection.safeSummary, "projection.safeSummary");
  }
  projection.changes.forEach((change, index) => {
    const changePath = `projection.changes[${index}]`;
    validateBoundedJson(change, changePath);
    if (!change || typeof change !== "object" || Array.isArray(change)) {
      throw new Error(`${changePath} must be an object`);
    }
    const rawChange = change as unknown as Record<string, unknown>;
    const kind = requireNonEmptyString(rawChange.kind, `${changePath}.kind`);

    if (kind === "object") {
      const operation = requireNonEmptyString(
        rawChange.operation,
        `${changePath}.operation`,
      );
      if (
        operation !== "created" &&
        operation !== "updated" &&
        operation !== "deleted"
      ) {
        throw new Error(
          `${changePath}.operation must be created, updated, or deleted`,
        );
      }
      if (operation === "deleted") {
        validateObjectReference(rawChange.target, `${changePath}.target`);
      } else {
        validateProjectedRecord(rawChange.record, `${changePath}.record`);
      }
    } else if (kind === "relationship") {
      const operation = requireNonEmptyString(
        rawChange.operation,
        `${changePath}.operation`,
      );
      if (operation !== "connected" && operation !== "disconnected") {
        throw new Error(
          `${changePath}.operation must be connected or disconnected`,
        );
      }
      requireNonEmptyString(
        rawChange.relationship,
        `${changePath}.relationship`,
      );
      validateObjectReference(rawChange.from, `${changePath}.from`);
      validateObjectReference(rawChange.to, `${changePath}.to`);
    } else if (kind === "state_observation") {
      if (rawChange.operation !== undefined) {
        throw new Error(
          `${changePath}.operation is not valid for an observation`,
        );
      }
      validateObjectReference(rawChange.target, `${changePath}.target`);
      requireNonEmptyString(rawChange.machine, `${changePath}.machine`);
      requireNonEmptyString(rawChange.state, `${changePath}.state`);
    } else {
      throw new Error(`${changePath}.kind is unsupported: ${kind}`);
    }
  });

  const objectOperations = new Map<
    string,
    { deleted: boolean; upserted: boolean }
  >();
  for (const change of projection.changes) {
    if (change.kind !== "object") continue;
    const reference =
      change.operation === "deleted" ? change.target : change.record;
    const key = `${reference.className}\u0000${reference.id}`;
    const operations = objectOperations.get(key) || {
      deleted: false,
      upserted: false,
    };
    if (change.operation === "deleted") operations.deleted = true;
    else operations.upserted = true;
    if (operations.deleted && operations.upserted) {
      throw new Error(
        `projection.changes cannot both delete and upsert ${reference.className}/${reference.id}; return only its canonical final state`,
      );
    }
    objectOperations.set(key, operations);
  }

  const outcome = (projection as TransitionProjectionResult).outcome;
  if (declaration.kind === "transition") {
    if (!outcome) {
      throw new Error("A transition projection must return an outcome");
    }
    requireNonEmptyString(outcome.key, "projection.outcome.key");
    if (outcome.error) {
      validateBoundedJson(outcome.error, "projection.outcome.error");
    }
  } else if ((projection as EffectProjectionResult).outcome !== undefined) {
    throw new Error("An effect projection cannot declare a transition outcome");
  }
}

/** Stable JSON used by tests, fingerprints, and source-event conflict checks. */
export function canonicalizeCommitValue(value: unknown): string {
  const normalize = (current: unknown): unknown => {
    if (
      current === null ||
      typeof current === "boolean" ||
      typeof current === "string"
    ) {
      return typeof current === "string" ? current.normalize("NFC") : current;
    }
    if (typeof current === "number") {
      if (!Number.isFinite(current)) {
        throw new Error("Cannot canonicalize a non-finite number");
      }
      return Object.is(current, -0) ? 0 : current;
    }
    if (Array.isArray(current)) return current.map(normalize);
    if (current && typeof current === "object") {
      return Object.fromEntries(
        Object.entries(current as Record<string, unknown>)
          .filter(([, item]) => item !== undefined)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([key, item]) => [key.normalize("NFC"), normalize(item)]),
      );
    }
    throw new Error(`Cannot canonicalize ${typeof current}`);
  };
  return JSON.stringify(normalize(value));
}
